import type { VideoEditor } from '@src/editor';
import type {
  TranscriptElement,
  TranscriptElementWithViewData,
} from '@src/types.ts/transcript';

export type TranscriptDiffElement = TranscriptElement & {
  originalValue?: string | null;
};

export type TranscriptElementBlock = {
  elements: TranscriptDiffElement[];
  state: TranscriptDiffElement['state'];
  indexOffset: number;
  /** Original block's indexOffset before paragraph splitting (used for selection lookups) */
  originalIndexOffset?: number;
  /** Character offset from the start of the original block (used for selection lookups in split blocks) */
  charOffset?: number;
};

export type TranscriptDiffSelectedElement = {
  element: TranscriptDiffElement;
  elementIndex: number;
  blockIndex: number;
};

export const checkMultipleWords = (element: TranscriptDiffElement) =>
  (element.value?.split(' ').length || 0) > 2;

// The punctuation-aware inline-spacing rule lives in the pure, layer-free
// `@src/utility/strings` so non-component layers (e.g. `stores-v2`) can reuse it
// without importing a component module. Re-exported here so the existing
// transcript callers stay unchanged.
export {
  joinPhraseValues,
  needsSpaceBetweenPhrases,
} from '@src/utility/strings';

/**
 * Single source of truth for "is this transcript element whitespace?"
 *
 * Detection is purely value-based: a punct element whose `value` trims to
 * empty (typically `" "`, `"\t"`, `"\n"`). This matches what
 * `TranscriptionText.elementPropsToWordClassName` already uses for the
 * source-language view and what `KaraokeLinesView` / `KaraokeBreaksView`
 * use for caption rendering — so source, redline, translated, and caption
 * views all classify whitespace the same way.
 *
 * NOTE: We deliberately do NOT key off `subtype === 'whitespace'`. Real
 * transcripts emit `{type: 'punct', value: ' '}` without a `subtype`
 * field, so any subtype-based predicate silently misses the actual data
 * shape (the bug that hid translated phrases led by a whitespace token
 * in PLAT-9618).
 */
export function isWhitespaceElement(el: {
  type?: string;
  value?: string | null;
}): boolean {
  return (
    el.type === 'punct' &&
    typeof el.value === 'string' &&
    el.value.length > 0 &&
    el.value.trim() === ''
  );
}

/**
 * PLAT-11703 — is `index` the first rendered token of a caption row?
 *
 * Only meaningful for the captions (karaoke) view, whose display array is the
 * karaoke lines flattened into one list with every token tagged by its row
 * (`viewData.lineId`). A row starts wherever the nearest rendered token to the
 * left belongs to a different row.
 *
 * Whitespace is skipped on the way left because a row's trailing space is
 * synthesized by `addSpaceToEndOfTranscriptElements` and then collapsed with
 * the next row's leading space by `dedupeConsecutiveWhitespace`, so which row
 * the surviving token claims is an artifact of that collapse, not a boundary.
 *
 * The first row of the transcript is not a row start: there is no row above it
 * to join to.
 */
export function isCaptionRowStart(
  elements: readonly TranscriptElementWithViewData[],
  index: number,
): boolean {
  const lineId = elements[index]?.viewData?.lineId;
  if (!lineId) return false;
  for (let i = index - 1; i >= 0; i--) {
    const el = elements[i];
    if (
      isWhitespaceElement(el) ||
      el.state === 'removed' ||
      el.state === 'cut'
    ) {
      continue;
    }
    return el.viewData?.lineId !== lineId;
  }
  return false;
}

/**
 * Collapse runs of consecutive whitespace tokens to a single token.
 *
 * Sources of consecutive whitespaces in the active transcript:
 *   1. `KaraokeStore.getKaraokeLinesFormatted` appends a synthetic
 *      trailing whitespace per line (`addSpaceToEndOfTranscriptElements`
 *      so `KaraokeLinesView` can break lines). Once `flatMap`'d, the
 *      synthetic trailing meets the next line's natural leading
 *      whitespace at every line boundary.
 *   2. `fillInWhitespacesInTranscriptElements` historically inserted
 *      synthetics around existing whitespace at cut/segment boundaries
 *      (PLAT-9618 hardened the predicate, but legacy data may still
 *      arrive in this shape).
 *
 * Both produce visible double spaces in the rendered text. Running this
 * pass once at the `displayElements` boundary keeps every downstream
 * renderer (transcription, redline diff, translated, captions) in sync.
 *
 * Real source tokens (`initial_index >= 0`) win over synthetics
 * (`initial_index === -1`) so cut/state markers carried by the real
 * token survive the collapse.
 */
export function dedupeConsecutiveWhitespace<
  T extends { type?: string; value?: string | null; initial_index?: number },
>(tokens: readonly T[]): T[] {
  if (tokens.length < 2) return tokens.slice();
  const out: T[] = [];
  for (const token of tokens) {
    const last = out[out.length - 1];
    if (last && isWhitespaceElement(token) && isWhitespaceElement(last)) {
      const lastIsSynthetic =
        typeof last.initial_index === 'number' && last.initial_index < 0;
      const tokenIsReal =
        typeof token.initial_index === 'number' && token.initial_index >= 0;
      if (lastIsSynthetic && tokenIsReal) {
        out[out.length - 1] = token;
      }
      continue;
    }
    out.push(token);
  }
  return out;
}

/**
 * Concatenates element `value`s across an inclusive [start, end] index range
 * into a single trimmed string. Used wherever a "whole-words" selected text
 * needs to be reconstructed from transcript elements (analytics payloads,
 * pull-quote save, etc.) — the browser selection alone can include partial
 * words, so callers prefer this index-based extraction.
 */
export function extractTranscriptSelectionText(
  elements: ReadonlyArray<{ value?: string | null }> | null | undefined,
  startIndex: number,
  endIndex: number,
): string {
  if (!elements) return '';
  return elements
    .slice(startIndex, endIndex + 1)
    .map(el => el.value ?? '')
    .join('')
    .trim();
}

/**
 * First-sentence preview of a longer text — used for the highlight-row hover
 * tooltip so a truncated highlight name still shows its opening line in full.
 * Returns the text up to and including the first sentence terminator
 * (`.`/`!`/`?`); when the opening sentence is longer than `maxChars`, or there
 * is no terminator, it ellipsizes on a word boundary (never mid-word).
 */
export function firstSentencePreview(text: string, maxChars = 160): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (!clean) return '';
  const sentence = clean.match(/^.*?[.!?](?=\s|$)/)?.[0] ?? clean;
  if (sentence.length <= maxChars) return sentence;
  const truncated = sentence.slice(0, maxChars);
  const lastSpace = truncated.lastIndexOf(' ');
  return `${truncated.slice(0, lastSpace > 0 ? lastSpace : maxChars).trim()}…`;
}

/**
 * Splits the transcript elements into blocks based on their state.
 * Replaced elements and elements with multiple words are treated as
 * a single block.
 */
export const getTranscriptElementBlocks = (
  elements: TranscriptDiffElement[],
  original: TranscriptDiffElement[],
  cursor?: { from: number; to: number },
) => {
  if (!elements.length || !original.length) return [];
  const blocks: TranscriptElementBlock[] = [];
  let currentBlock: TranscriptDiffElement[] = [];
  let currentState: TranscriptDiffElement['state'] = elements[0].state;

  // Treat 'muted' as 'added' for block-splitting so hidden-caption elements
  // (e.g. commas) don't create their own block and cause unwanted line breaks
  const normalizeStateForSplit = (
    s: TranscriptDiffElement['state'],
  ): TranscriptDiffElement['state'] => (s === 'muted' ? undefined : s);

  elements.forEach((el, index, arr) => {
    // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/naming-convention
    const _indexOffset = index - currentBlock.length;
    const indexOffset =
      cursor && _indexOffset > cursor.from ? _indexOffset - 1 : _indexOffset;

    // PLAT-6329: For removed/cut elements, preserve the original timestamp if current ts is null/undefined
    // This ensures deleted content still has timing info for paragraph break display in Redline view
    // Note: TranscriptTextElement type has `ts: number` but runtime value can be null for removed elements
    let processedEl: TranscriptDiffElement = el;
    if ((el.state === 'removed' || el.state === 'cut') && el.type === 'text') {
      const originalEl = original[el.initial_index];
      // Use type assertion to check ts which may be null at runtime despite type saying number
      const currentTs = (el as unknown as { ts: number | null }).ts;
      if (originalEl && originalEl.type === 'text' && currentTs == null) {
        processedEl = { ...el, ts: originalEl.ts, end_ts: originalEl.end_ts };
      }
    }

    if (
      el.state === 'replaced' || // cut replaced element block
      checkMultipleWords(el) || // cut pre-multiple word element block
      (currentBlock.length === 1 && checkMultipleWords(currentBlock[0])) || // cut post-multiple word element block
      normalizeStateForSplit(el.state) !== normalizeStateForSplit(currentState) // cut state change element block
    ) {
      blocks.push({
        elements: currentBlock,
        state: currentState,
        indexOffset,
      });
      currentBlock =
        el.state === 'replaced'
          ? [
              {
                ...processedEl,
                originalValue: original[el.initial_index]?.value,
              },
            ]
          : [processedEl];
      currentState = el.state;
    } else {
      currentBlock.push(processedEl);
      if (index === arr.length - 1) {
        blocks.push({
          elements: currentBlock,
          state: currentState,
          indexOffset,
        });
      }
    }
  });

  return blocks.filter(block => block.elements.length > 0);
};

export const groupTranscriptElementsByTimelineElement = (
  transcriptElements: TranscriptElement[],
  editor: VideoEditor,
  videoTrackId: string,
): Record<string, TranscriptElement[]> => {
  // Iterate through the transcript elements and group them by their timeline element
  const groupedElements: Record<string, TranscriptElement[]> = {};
  transcriptElements.forEach(element => {
    // Skip elements without a timestamp (ie punctuation)
    if (!element.ts) {
      return;
    }

    const timelineElement = editor.getElementAtTime(videoTrackId, element.ts);
    if (timelineElement) {
      const timelineElementId = timelineElement.id;
      if (!groupedElements[timelineElementId]) {
        groupedElements[timelineElementId] = [];
      }
      groupedElements[timelineElementId].push(element);
    } else {
      console.warn(
        `No timeline element found for transcript element with ts: ${element.ts}`,
      );
    }
  });

  return groupedElements;
};

export const getTranscriptElementFromCharIndex = (
  charIndex: number,
  elements?: TranscriptDiffElement[],
) => {
  if (!elements) return undefined;
  let length = 0;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < elements.length; i++) {
    const elementCharIndex = length;
    length += elements[i].value?.length || 0;
    // Use strict < so a charIndex at the boundary between two elements
    // resolves to the next element (e.g. selecting a comma after a word).
    // Fall back to the last element when charIndex equals total length.
    if (charIndex < length || i === elements.length - 1) {
      return {
        element: elements[i],
        elementIndex: i,
        elementCharIndex,
      };
    }
  }
  return undefined;
};

/**
 * Compute the block-relative character offset at a given screen point.
 * Uses caretPositionFromPoint to find the text node, then walks preceding
 * siblings up to the block span (identified by data-block-index) to accumulate
 * the full offset from the block's start.
 *
 * Returns the resolved element match and the block it belongs to, or null if
 * resolution fails (e.g., click landed outside a block span).
 */
export const resolveBlockElementAtPoint = (
  x: number,
  y: number,
  blocks: TranscriptElementBlock[],
  knownBlock?: TranscriptElementBlock,
): {
  block: TranscriptElementBlock;
  element: TranscriptDiffElement;
  elementIndex: number;
  elementCharIndex: number;
} | null => {
  const mousePos = document.caretPositionFromPoint?.(x, y);
  const offsetNode = mousePos?.offsetNode;
  if (!mousePos || !offsetNode || offsetNode.nodeType !== Node.TEXT_NODE) {
    return null;
  }

  // Find the ancestor block span (has data-block-index)
  let blockSpan: HTMLElement | null = null;
  let el: HTMLElement | null = offsetNode.parentElement;
  while (el) {
    if (el.dataset.blockIndex !== undefined) {
      blockSpan = el;
      break;
    }
    el = el.parentElement;
  }
  if (!blockSpan) return null;

  // Compute block-relative character offset by walking from the text node
  // up to the block span, summing preceding siblings at each level.
  let charOffset = mousePos.offset;
  let current: Node | null = offsetNode;
  while (current && current !== blockSpan) {
    let sibling = current.previousSibling;
    while (sibling) {
      charOffset += sibling.textContent?.length || 0;
      sibling = sibling.previousSibling;
    }
    current = current.parentNode;
  }

  // Use the known block when provided (O(1)), otherwise fall back to a linear
  // scan — necessary when the caller doesn't know which block the point landed in.
  let block: TranscriptElementBlock | undefined;
  if (knownBlock) {
    block = knownBlock;
  } else {
    const blockIndex = Number(blockSpan.dataset.blockIndex);
    const blockCharOffset = Number(blockSpan.dataset.charOffset || '0');
    block = blocks.find(
      b =>
        (b.originalIndexOffset ?? b.indexOffset) === blockIndex &&
        (b.charOffset ?? 0) === blockCharOffset,
    );
  }
  if (!block) return null;

  const match = getTranscriptElementFromCharIndex(charOffset, block.elements);
  if (!match) return null;

  return {
    block,
    element: match.element,
    elementIndex: match.elementIndex,
    elementCharIndex: match.elementCharIndex,
  };
};

/**
 * Groups transcript element blocks by paragraph_id for rendering with paragraph breaks.
 * This function SPLITS blocks at paragraph boundaries, so even a single block with
 * elements from multiple paragraphs will be split into multiple blocks.
 * Returns an array of paragraph groups, where each group contains blocks that belong
 * to the same paragraph.
 */
export type ParagraphBlockGroup = {
  paragraphId: string | undefined;
  blocks: TranscriptElementBlock[];
};

export const groupBlocksByParagraph = (
  blocks: TranscriptElementBlock[],
): ParagraphBlockGroup[] => {
  if (blocks.length === 0) return [];

  // Check if paragraph grouping is meaningful
  const allBlockElements = blocks.flatMap(block => block.elements);
  const textElements = allBlockElements.filter(el => el.type === 'text');
  const textElementsWithParagraphId = textElements.filter(
    el => el.paragraph_id,
  );

  if (textElementsWithParagraphId.length === 0) {
    // No paragraph_ids, return all blocks in single group
    return [{ paragraphId: undefined, blocks }];
  }

  const uniqueParagraphIds = new Set(
    textElementsWithParagraphId.map(el => el.paragraph_id),
  );

  const avgElementsPerParagraph =
    textElementsWithParagraphId.length / uniqueParagraphIds.size;

  if (avgElementsPerParagraph < 3) {
    // Not meaningful grouping
    return [{ paragraphId: undefined, blocks }];
  }

  // Split blocks at paragraph boundaries and group them
  const groups: ParagraphBlockGroup[] = [];
  let currentGroup: ParagraphBlockGroup | null = null;
  let lastTextParagraphId: string | undefined = undefined;

  blocks.forEach(block => {
    // Track the current paragraph within this block
    let currentBlockParagraphId: string | undefined = lastTextParagraphId;
    let currentBlockElements: TranscriptDiffElement[] = [];
    let currentBlockStartOffset = block.indexOffset;
    // Track character offset from start of original block for selection lookups
    let currentBlockCharOffset = 0;
    let totalCharOffset = 0;

    const flushCurrentBlock = () => {
      if (currentBlockElements.length > 0) {
        const newBlock: TranscriptElementBlock = {
          elements: currentBlockElements,
          state: block.state,
          indexOffset: currentBlockStartOffset,
          // Preserve original block's indexOffset for selection lookups
          originalIndexOffset: block.indexOffset,
          // Store character offset from start of original block
          charOffset: currentBlockCharOffset,
        };

        if (
          !currentGroup ||
          currentGroup.paragraphId !== currentBlockParagraphId
        ) {
          currentGroup = {
            paragraphId: currentBlockParagraphId,
            blocks: [],
          };
          groups.push(currentGroup);
        }
        currentGroup.blocks.push(newBlock);
      }
    };

    // Track if previous element was a newline (to split paragraph on next text element)
    let splitAfterNewline = false;
    let lastTextSpeakerLabel: string | undefined = undefined;
    // Track the speaker label of the current block's first text element (O(1) comparison)
    let currentBlockSpeakerLabel: string | undefined = undefined;

    block.elements.forEach((element, elementIndex) => {
      // Determine paragraph_id: use element's paragraph_id for text elements,
      // or inherit from last text element for non-text elements
      const elementParagraphId =
        element.type === 'text' ? element.paragraph_id : lastTextParagraphId;
      const elementSpeakerLabel =
        element.type === 'text' ? element.speaker_label : lastTextSpeakerLabel;

      if (element.type === 'text' && element.paragraph_id) {
        lastTextParagraphId = element.paragraph_id;
      }
      if (element.type === 'text' && element.speaker_label) {
        lastTextSpeakerLabel = element.speaker_label;
      }

      // Check if we need to start a new block due to:
      // 1. paragraph_id change
      // 2. newline character (split on next text element after newline)
      // 3. speaker_label change (section override)
      const isParagraphChange = elementParagraphId !== currentBlockParagraphId;
      const isNewlineTriggeredSplit =
        splitAfterNewline && element.type === 'text';
      const isSpeakerChange =
        element.type === 'text' &&
        elementSpeakerLabel !== undefined &&
        currentBlockSpeakerLabel !== undefined &&
        currentBlockSpeakerLabel !== elementSpeakerLabel;

      if (
        currentBlockElements.length > 0 &&
        (isParagraphChange || isNewlineTriggeredSplit || isSpeakerChange)
      ) {
        // Flush the current block and start a new one
        flushCurrentBlock();
        currentBlockElements = [];
        currentBlockStartOffset = block.indexOffset + elementIndex;
        // Record the character offset where this new split block starts
        currentBlockCharOffset = totalCharOffset;
        currentBlockParagraphId = elementParagraphId;
        currentBlockSpeakerLabel = elementSpeakerLabel;
        splitAfterNewline = false;
      }

      // Initialize paragraph ID, speaker label, and char offset for first element
      if (currentBlockElements.length === 0) {
        currentBlockParagraphId = elementParagraphId;
        currentBlockSpeakerLabel = elementSpeakerLabel;
        currentBlockCharOffset = totalCharOffset;
      }

      currentBlockElements.push(element);
      // Track total character offset through the original block
      totalCharOffset += element.value?.length || 0;

      // If this element is a newline, flag to split on next text element
      if (element.value === '\n') {
        splitAfterNewline = true;
      }
    });

    // Flush remaining elements in the block
    flushCurrentBlock();
  });

  return groups;
};

/**
 * Vertical position (as a fraction of container height from the top) where
 * the playhead element should land after auto-scroll. 0.15 = third line from
 * the top, so ~2 lines of context remain visible above the cursor.
 *
 * Single source of truth for all transcript views (TranscriptionText /
 * _TranscriptionDiff via Transcript.tsx, TranslatedTranscriptView,
 * TranslatedCaptionsView). Change here and every view follows.
 */
export const PLAYHEAD_TOP_OFFSET_RATIO = 0.15;

/**
 * Minimum offset (in line-heights) from the top of the visible scroll area
 * to where the cursor is allowed to land on a view-switch (redline ⇌
 * regular, translated regular ⇌ translated redline). Floors:
 *   1. The locked Y captured by `captureCursorYLock` so the post-toggle
 *      restore can't pin the cursor right at the top of the new view.
 *   2. The fallback "open-anchor" position used when no previous Y was
 *      captured (e.g. the very first time a view renders) and the cursor
 *      would otherwise be off-screen.
 *
 * Single source of truth: change here and `Transcript.tsx`,
 * `TranslatedTranscriptView.tsx`, and CLAUDE.md's "≥ 3 line-heights"
 * references all follow.
 */
export const CURSOR_TOP_OFFSET_LINES = 3;

/**
 * Read the computed CSS `line-height` off `el` in pixels. Falls back to
 * `1.4 × glyph-rect-height` (a typical ratio between text-glyph height
 * and CSS line-box height) when the computed value isn't a finite
 * positive number — e.g. when the element inherits `line-height: normal`
 * or the property hasn't yet been applied. Final fallback: 24px.
 *
 * Single source of truth so all the view-switch anchor math agrees on
 * what "one line" means.
 */
export const computeLineHeightPx = (el: Element): number => {
  const computed = parseFloat(window.getComputedStyle(el).lineHeight);
  if (Number.isFinite(computed) && computed > 0) return computed;
  const rectHeight = el.getBoundingClientRect().height;
  return rectHeight * 1.4 || 24;
};

/**
 * Marks a row that pins itself to the top of the scroll container — currently
 * the reel transcript's Chapter heading (PLAT-11253 DS2).
 *
 * A pinned row covers the top of the container, so a word scrolled to a small
 * offset arrives *underneath* it: invisible, with the scroll reporting success.
 * Every scroll-to-a-word therefore measures what is pinned and lands below it
 * (epic PLAT-11155 DE13, constraint 2).
 */
export const STICKY_HEADING_ATTR = 'data-transcript-sticky-heading';

/**
 * How much of `container`'s top is currently covered by pinned rows.
 *
 * **Measured, never assumed** — the row's height depends on its content (a
 * chapter title can wrap) and on whether one is pinned at all right now. A
 * container with no such rows measures 0, which is every transcript surface
 * outside the reel structure: their scroll math is byte-identical to before.
 *
 * "Pinned" is read off geometry rather than off `position: sticky`, because a
 * sticky row that has not reached the top yet is an ordinary row in the flow
 * and must not be counted — the words beside it are perfectly visible.
 */
export const measureStickyTopInset = (container: Element): number => {
  const containerTop = container.getBoundingClientRect().top;
  let inset = 0;
  for (const row of container.querySelectorAll(`[${STICKY_HEADING_ATTR}]`)) {
    const rect = row.getBoundingClientRect();
    // Stuck iff its top has caught up with the container's. The 1px tolerance
    // absorbs sub-pixel layout; anything further down is still scrolling.
    if (rect.top - containerTop > 1) continue;
    inset = Math.max(inset, rect.bottom - containerTop);
  }
  return Math.max(0, inset);
};

/**
 * Compute the `scrollTop` value that places `el` at `ratio` of `container`'s
 * viewport from the top, below anything pinned over it.
 *
 * Defaults to `PLAYHEAD_TOP_OFFSET_RATIO`, the playback anchor every view
 * shares. A *jump* passes `CURSOR_JUMP_TOP_OFFSET_RATIO` instead — see below
 * for why the two differ.
 *
 * The ratio is applied to the space actually left over, not to the whole
 * container: with a heading pinned, "15% down" means 15% of what the creator
 * can see, otherwise the anchor drifts down the panel by the heading's height
 * every time one is pinned.
 */
export const computePlayheadScrollTop = (
  container: Element,
  el: Element,
  ratio: number = PLAYHEAD_TOP_OFFSET_RATIO,
): number => {
  const containerRect = container.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const inset = measureStickyTopInset(container);
  return (
    container.scrollTop +
    (elRect.top - containerRect.top) -
    inset -
    (containerRect.height - inset) * ratio
  );
};

/**
 * Where a **jump** lands the cursor: a third of the way down the panel.
 *
 * Deliberately lower than `PLAYHEAD_TOP_OFFSET_RATIO`, and for a different
 * reason. Playback anchors near the top because the words that matter are the
 * ones about to be spoken, so the panel is spent on read-ahead. A jump is the
 * opposite errand: the creator has been sent to a passage to *work on it*, and
 * needs to see where it starts — which means context above it — with most of
 * the panel still below to read and edit forward into (PLAT-11061).
 */
export const CURSOR_JUMP_TOP_OFFSET_RATIO = 1 / 3;

/**
 * The band a jump leaves alone: between a tenth and three-fifths of the way
 * down. A target already sitting in here is comfortably readable, and
 * re-anchoring it would be a jolt for no gain.
 *
 * The bottom edge is what the plain "is it inside the container at all" test
 * got wrong. A word five pixels above the fold passes that test and is useless
 * — nothing of the passage follows it on screen — so the jump reported success
 * and left the creator staring at the last line of the panel (PLAT-11061).
 */
export const CURSOR_JUMP_COMFORT_BAND = { top: 0.1, bottom: 0.6 } as const;

/**
 * Whether `cursorRect` is placed well enough that a jump should not move the
 * view. Wholly visible *and* inside {@link CURSOR_JUMP_COMFORT_BAND}.
 *
 * `topInset` is the height a pinned heading covers the container's top with
 * ({@link measureStickyTopInset}). It moves the effective top edge down, so a
 * word hidden *behind* the heading counts as not visible rather than as
 * comfortably placed — the band's own top ratio (0.1) is smaller than a pinned
 * chapter heading on a short panel, so without this the jump congratulates
 * itself on a word the creator cannot see. Defaults to 0, which is every
 * surface with nothing pinned.
 */
export const isCursorComfortablyPlaced = (
  containerRect: { top: number; bottom: number; height: number },
  cursorRect: { top: number; bottom: number },
  topInset: number = 0,
): boolean => {
  const visibleTop = containerRect.top + topInset;
  const visibleHeight = containerRect.height - topInset;
  if (
    visibleHeight <= 0 ||
    cursorRect.top < visibleTop ||
    cursorRect.bottom > containerRect.bottom
  ) {
    return false;
  }
  const offset = (cursorRect.top - visibleTop) / visibleHeight;
  return (
    offset >= CURSOR_JUMP_COMFORT_BAND.top &&
    offset <= CURSOR_JUMP_COMFORT_BAND.bottom
  );
};

/**
 * Bring `cursorTo` into a *readable* position — the scroll a jump performs,
 * which is not the one playback performs (PLAT-11061).
 *
 * `scrollToPlayhead` anchors the words about to be spoken near the top and
 * treats "anywhere inside the container" as placed well enough. Both are right
 * for playback and wrong for being sent somewhere to work: a word resting a few
 * pixels above the fold passes that test and is useless, so the jump reported
 * success while leaving the creator on the panel's last line with none of the
 * passage following it.
 *
 * So the view is left alone only while the cursor is inside
 * {@link CURSOR_JUMP_COMFORT_BAND}, and otherwise anchored at
 * {@link CURSOR_JUMP_TOP_OFFSET_RATIO}.
 *
 * `container` is passed in rather than looked up: two transcripts coexist with
 * the clip preview and both carry `#transcript-scrollable-container`, so a
 * global lookup would scroll the wrong one (`docs/transcript-ui-patterns.md`).
 * Uses the same finders as the playback path, so both attribute schemes
 * (`data-index` and the translated views' `data-initial-index`) resolve. Never
 * calls `scrollIntoView` — that would scroll the modal too — and never moves
 * focus, so a jump cannot pull focus off the renderer canvas and revert an
 * in-flight edit (PLAT-10700).
 *
 * Returns `false` when the row has not been rendered yet, so a caller that has
 * a retry can use it; the view is left alone rather than guessed at.
 */
export const scrollCursorIntoJumpView = (
  container: HTMLElement,
  cursorTo: number,
  elements:
    | { initial_index: number; source_uuid?: string; id?: string }[]
    | undefined,
): boolean => {
  const cursorEl =
    findVisibleByDataIndex(container, cursorTo) ??
    findByForwardInitialIndexScan(container, cursorTo, elements) ??
    findVisibleDiffBlockForIndex(container, cursorTo);
  if (!cursorEl) return false;

  const containerRect = container.getBoundingClientRect();
  if (
    !isCursorComfortablyPlaced(
      containerRect,
      cursorEl.getBoundingClientRect(),
      measureStickyTopInset(container),
    )
  ) {
    bypassScrollBlocker(container, {
      top: computePlayheadScrollTop(
        container,
        cursorEl,
        CURSOR_JUMP_TOP_OFFSET_RATIO,
      ),
    });
  }
  return true;
};

/**
 * Lock-restore decision: given a captured Y offset and the new view's
 * cursor element (or null if not yet measurable), decide what
 * `scrollToPlayhead` should do.
 *
 * - `{ kind: 'apply', scrollTop }` — write `scrollTop` to the container
 *   and clear the lock.
 * - `{ kind: 'preserve-lock' }` — caller's responsibility: leave the
 *   lock untouched and arrange a retry (used by the synchronous
 *   `tryLockOnly=true` pass when the just-mounted view's layout isn't
 *   settled yet).
 * - `{ kind: 'fall-through' }` — clear the lock and fall through to the
 *   non-lock anchor path (e.g. lookahead at PLAYHEAD_TOP_OFFSET_RATIO).
 *
 * Pure function; takes only the geometry it needs and a couple of flags.
 * Lets the host (`scrollToPlayhead`) stay readable by handing the
 * branching off here, and lets us unit-test the lock semantics without
 * mounting the full `Transcript` component.
 */
export type LockRestoreDecision =
  | { kind: 'apply'; scrollTop: number }
  | { kind: 'preserve-lock' }
  | { kind: 'fall-through' };

export const computeLockRestoreDecision = (
  containerScrollTop: number,
  containerRect: { top: number },
  cursorRect: { top: number } | null,
  lockedOffset: number | null,
  tryLockOnly: boolean,
): LockRestoreDecision => {
  if (lockedOffset === null) return { kind: 'fall-through' };
  if (cursorRect !== null) {
    const scrollTop =
      containerScrollTop + (cursorRect.top - containerRect.top) - lockedOffset;
    return { kind: 'apply', scrollTop };
  }
  if (tryLockOnly) return { kind: 'preserve-lock' };
  return { kind: 'fall-through' };
};

/**
 * Find the first element matching `[data-index='${index}']` inside `container`
 * that has a non-zero rendered height.
 *
 * Required because Transcript.tsx keeps both TranscriptionText and
 * TranscriptionDiffNew mounted (one with `display: none`), so the same
 * `data-index` attribute is present twice. A naive `querySelector` returns
 * whichever appears first in DOM order, which may be the hidden one whose
 * `getBoundingClientRect()` is `{0,0,0,0}` — useless for scroll math.
 */
export const findVisibleByDataIndex = (
  container: Element,
  index: number,
): Element | null => {
  const matches = container.querySelectorAll(`[data-index='${index}']`);
  for (const el of matches) {
    if (el.getBoundingClientRect().height > 0) return el;
  }
  return null;
};

/**
 * Translated-view scroll fallback: walk forward through `elements` from
 * `startIndex` looking for the first element whose `initial_index` is
 * rendered as a phrase lead (`data-initial-index` attribute) in the DOM.
 *
 * Translated views (`TranslatedView` / `TranslatedRedlineView`) tag spans
 * with `data-initial-index` (the phrase lead's source index), not
 * `data-index` (array slot). Scroll-target lookups via
 * `findVisibleByDataIndex` return null in those views — the cursor
 * never auto-scrolls. This fallback restores parity by scanning forward
 * for any rendered phrase lead within a small horizon.
 */
/**
 * Forward-scan horizon for `findByForwardInitialIndexScan`. Bounded so
 * the worst-case scan stays O(1) per call. 30 source positions covers
 * the largest single phrase observed in production data (multi-clause
 * sentences split by the LLM); raising it has diminishing returns and
 * costs querySelectorAll passes per missed lookup.
 */
const FORWARD_INITIAL_INDEX_SCAN_HORIZON = 30;

export const findByForwardInitialIndexScan = (
  container: Element,
  startIndex: number,
  // PLAT-10611 (F5) — accepts the source elements' stable id (`source_uuid ?? id`)
  // so the scan resolves each candidate word by identity FIRST (drift-correct),
  // falling back to the positional `initial_index`.
  elements:
    | { initial_index: number; source_uuid?: string; id?: string }[]
    | undefined,
  horizonAhead: number = FORWARD_INITIAL_INDEX_SCAN_HORIZON,
): Element | null => {
  if (!elements) return null;
  const end = Math.min(elements.length, startIndex + horizonAhead);
  for (let i = startIndex; i < end; i++) {
    const el = elements[i];
    const idx = el?.initial_index;
    if (idx === undefined || idx < 0) continue;
    // PLAT-10611 (F5, R5) — id-first, integer fallback (§6).
    const uuid = el?.source_uuid ?? el?.id;
    const matches = uuid
      ? (() => {
          const byId = container.querySelectorAll(
            `[data-source-uuid='${uuid}']`,
          );
          return byId.length > 0
            ? byId
            : container.querySelectorAll(`[data-initial-index='${idx}']`);
        })()
      : container.querySelectorAll(`[data-initial-index='${idx}']`);
    for (const m of matches) {
      if ((m as HTMLElement).getBoundingClientRect().height > 0) {
        return m;
      }
    }
  }
  return null;
};

/**
 * Window event dispatched by callers that are about to mutate state which
 * causes the transcript view to re-render (e.g. switching language). When
 * the event fires, the active `Transcript` instance measures the current
 * cursor's Y offset within the scroll container and stores it so the
 * post-render scroll anchor lands on the same line — preserving the
 * reader's vantage point.
 *
 * Dispatch this synchronously *before* the state change, while the OLD
 * DOM is still measurable.
 */
export const REQUEST_CURSOR_Y_LOCK_EVENT = 'arbor:request-cursor-y-lock';

/**
 * Find the cursor element across all transcript views.
 *
 * The original-language views (TranscriptionText / _TranscriptionDiff) tag
 * elements with `data-index` (the array slot in `transcriptElements`).
 * The translated views (TranslatedTranscriptView / TranslatedCaptionsView)
 * tag with `data-initial-index` (the source element's `initial_index`).
 *
 * To anchor scroll across a view switch — including switches that change
 * which attribute is in use — we look up by both keys and take the first
 * visible match. Pass both values; whichever attribute the active view
 * emits will resolve.
 */
/**
 * Diff-view fallback: find the visible block span whose element-index range
 * `[data-index, data-index-end)` contains `cursorIndex`.
 *
 * The redline view (`_TranscriptionDiff`) groups consecutive same-state
 * elements into one block span. Only the block's FIRST element index is
 * exposed as `data-index`; the rest live implicitly inside the range up to
 * `data-index-end`. So `findVisibleByDataIndex` (exact match) returns null
 * whenever the cursor sits mid-block — which is the common case for kept
 * paragraphs. This scan finds the containing block instead, letting
 * lock-restore math anchor on a real DOM node.
 *
 * The `[data-index][data-index-end]` selector is diff-specific: regular
 * (`TranscriptionText`) and translated views never emit `data-index-end`,
 * so there's no risk of returning a non-diff element from this fallback.
 */
export const findVisibleDiffBlockForIndex = (
  container: Element,
  cursorIndex: number,
): Element | null => {
  const blocks = container.querySelectorAll('[data-index][data-index-end]');
  for (const el of blocks) {
    const startAttr = el.getAttribute('data-index');
    const endAttr = el.getAttribute('data-index-end');
    if (startAttr === null || endAttr === null) continue;
    const start = Number(startAttr);
    const end = Number(endAttr);
    if (Number.isNaN(start) || Number.isNaN(end)) continue;
    if (cursorIndex < start || cursorIndex >= end) continue;
    if ((el as HTMLElement).getBoundingClientRect().height > 0) {
      return el;
    }
  }
  return null;
};

export const findVisibleCursorElement = (
  container: Element,
  cursorIndex: number,
  initialIndex: number | undefined,
  // PLAT-10611 (F5, R6) — the cursor element's stable id (`source_uuid ?? id`).
  // The translated-view lookup resolves it by `data-source-uuid` FIRST
  // (drift-correct), falling back to `data-initial-index`.
  sourceUuid?: string,
): Element | null => {
  const byDataIndex = findVisibleByDataIndex(container, cursorIndex);
  if (byDataIndex) return byDataIndex;
  if (sourceUuid !== undefined) {
    const matches = container.querySelectorAll(
      `[data-source-uuid='${sourceUuid}']`,
    );
    for (const el of matches) {
      if (el.getBoundingClientRect().height > 0) return el;
    }
  }
  if (initialIndex !== undefined) {
    const matches = container.querySelectorAll(
      `[data-initial-index='${initialIndex}']`,
    );
    for (const el of matches) {
      if (el.getBoundingClientRect().height > 0) return el;
    }
  }
  // Redline (diff) view: blocks tag only their start with `data-index`, so
  // mid-block cursors aren't matched by the exact lookups above. Fall back
  // to the diff-range scan so toggling redline mid-paragraph still anchors
  // the cursor at the locked Y instead of dropping the view to scrollTop=0.
  return findVisibleDiffBlockForIndex(container, cursorIndex);
};

/**
 * Native `scrollTo` call that bypasses any monkey-patched override on the
 * element (e.g. the redline scroll-blocker installed in Transcript.tsx).
 * Use only for explicit user-initiated or programmatic anchoring scrolls
 * that must run regardless of playback state.
 */
export const bypassScrollBlocker = (
  container: Element,
  options: ScrollToOptions,
): void => {
  // Cast through `unknown` to satisfy the overloaded signature without losing
  // type safety at the callsite.
  (
    Element.prototype.scrollTo as unknown as (
      this: Element,
      options: ScrollToOptions,
    ) => void
  ).call(container, options);
};

/**
 * Whether a hover-driven `.focus()` on the transcript's shortcut-scope
 * container would take focus off something that needs it (`Transcript`'s
 * `mouseEnter`). `Timeline`'s own `mouseEnter` still guards the iframe case
 * inline; it has no inline rename to protect, so it was left alone.
 *
 * Two cases, one rule — never steal focus from:
 *  - the **renderer iframe**, while the creator is editing on the canvas. The
 *    iframe-deactivation reaction dispatches FORCED_REFRESH, whose
 *    `refreshTimeline` can revert an in-flight canvas edit (PLAT-10700). The
 *    renderer canvas is the app's only iframe.
 *  - a **field being typed in**. A Section heading's inline rename
 *    (PLAT-11257) opens from a portaled menu, so the pointer re-enters the
 *    transcript the instant that menu unmounts — and a blur on that field
 *    commits and closes it, before a single key is pressed.
 */
export const isFocusHeldElsewhere = (): boolean => {
  const active = document.activeElement;
  return (
    active instanceof HTMLIFrameElement ||
    active instanceof HTMLInputElement ||
    active instanceof HTMLTextAreaElement
  );
};
